import { motion } from "framer-motion";
import { Server, RefreshCcw, Wrench, TrendingUp, Settings, BarChart3 } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const cardReveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const steps = [
  {
    icon: Server,
    num: "01",
    title: "Machine Deployment",
    desc: "Strategic placement of high-performance dispensing machines at high-footfall locations across India.",
  },
  {
    icon: RefreshCcw,
    num: "02",
    title: "Recurring Syrup Revenue",
    desc: "Proprietary, India-focused syrup formulations create a consistent, recurring revenue stream.",
  },
  {
    icon: Wrench,
    num: "03",
    title: "Service & Maintenance",
    desc: "Complete service and maintenance network ensuring maximum uptime and client satisfaction.",
  },
  {
    icon: TrendingUp,
    num: "04",
    title: "High Margins",
    desc: "Low input and logistics costs combined with premium retail pricing deliver strong profit margins.",
  },
  {
    icon: Settings,
    num: "05",
    title: "Customisable Solutions",
    desc: "Tailored beverage solutions for each client, from flavour profiles to machine configurations.",
  },
  {
    icon: BarChart3,
    num: "06",
    title: "Scalable Distribution",
    desc: "Built to scale from a single location to a nationwide network efficiently.",
  },
];

export default function BusinessModelSection() {
  return (
    <section id="business-model" data-testid="business-model-section" className="py-16 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mb-16"
        >
          <motion.p variants={fadeUp} custom={0} className="text-[#C8D616] font-semibold text-sm uppercase tracking-widest mb-3">
            Business Model
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1D3A] leading-tight mb-6">
            A Scalable, High-Margin Ecosystem
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base lg:text-lg text-slate-600 leading-relaxed">
            Our business model is driven by branding, strategic machine placement, proprietary syrup
            formulations, and ongoing product innovation &mdash; creating a self-sustaining, scalable
            beverage ecosystem with strong recurring revenue.
          </motion.p>
        </motion.div>

        {/* Steps Grid - staggered reveal */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              variants={cardReveal}
              custom={i}
              className="relative p-8 rounded-2xl border border-slate-100 bg-white hover:shadow-xl hover:shadow-blue-500/5 hover:border-blue-100 transition-all duration-500 group"
            >
              <div className="absolute top-6 right-6 text-5xl font-extrabold text-slate-100 group-hover:text-[#C8D616]/20 transition-colors duration-500">
                {step.num}
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#0B1D3A] flex items-center justify-center mb-5 group-hover:bg-[#C8D616] transition-colors duration-500">
                <step.icon size={22} className="text-white group-hover:text-[#0B1D3A] transition-colors duration-500" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1D3A] mb-3">{step.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
